import { useCallback, useMemo } from 'react';

import {
  ADJUVANT_STEP_GROUP_KEY,
  BIOSTIMULANT_STEP_GROUP_KEY,
  OPERATION_STEP_GROUP_KEY,
  PLANTPROTECTION_STEP_GROUP_KEY,
  SEED_STEP_GROUP_KEY,
  SOILINPUT_STEP_GROUP_KEY,
} from 'constants/uiConstants';
import { InputTaskType } from 'interfaces/IInputTask';

import { StepSelectorGroup, StepSelectorOption } from './stepConfig';
import { useBiostimulantCatalogue } from './useBiostimulantCatalogue';
import { useOperationCatalogue } from './useOperationCatalogue';
import {
  useAdjuvantCatalogue,
  useChemicalCatalogue,
} from './usePlantProtectionCatalogue';
import { useSeedCatalogue } from './useSeedCatalogue';
import { useSoilInputCatalogue } from './useSoilInputCatalogue';

interface StepCatalogueState {
  isLoading: boolean;
  isSearchFetching: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  isFetchingNextPage: boolean;
  hasFetchNextPageError: boolean;
}

interface UseStepCatalogueOptionsResult {
  isCatalogueGroup: boolean;
  catalogue: StepCatalogueState;
  filteredOptions: StepSelectorOption[];
  clearAllSearches: () => void;
  onSearchForKey: (groupKey: string, value: string) => void;
}

// Owns everything about "what options does the active category show": which
// groups are live-catalogue-backed (Operation/Plant Protection/Adjuvant/Soil
// Input/Biostimulant/Seed) vs. still-dummy client-side-filtered (Production/
// Seed Dress), and the search wiring shared across all six catalogue hooks.
// Each catalogue hook is enabled only for the task types that actually use it
// (Plant Protection/Soil Input/Biostimulant → Application + Crop Establishment;
// Adjuvant → Application; Seed → Crop Establishment; Operation → all tasks).
// Plant Protection and Adjuvant have separate hooks over the same collection
// (OP-2420) so each tab searches independently. Passing null gOrgId to disabled
// hooks prevents their G-browse and MSearch queries from firing at all.
// Searching is lazy: only the active tab's catalogue is searched when the user
// types. Other tabs are searched when the user switches to them.
export const useStepCatalogueOptions = (
  gOrgId: string | null,
  activeGroup: StepSelectorGroup | undefined,
  searchText: string,
  taskType: InputTaskType,
  selectedSeedId: string | null = null
): UseStepCatalogueOptionsResult => {
  // Scope live catalogue data to the task types that can actually show each
  // group — prevents unnecessary G-browse and MSearch calls.
  const applicationOrgId =
    taskType === InputTaskType.Application ||
    taskType === InputTaskType.CropEstablishment
      ? gOrgId
      : null;
  const seedOrgId =
    taskType === InputTaskType.CropEstablishment ? gOrgId : null;

  const chemicalCatalogue = useChemicalCatalogue(applicationOrgId);
  const adjuvantCatalogue = useAdjuvantCatalogue(applicationOrgId);
  const operationCatalogue = useOperationCatalogue(gOrgId);
  const soilInputCatalogue = useSoilInputCatalogue(applicationOrgId);
  const biostimulantCatalogue = useBiostimulantCatalogue(applicationOrgId);
  const seedCatalogue = useSeedCatalogue(seedOrgId, selectedSeedId);

  const catalogueGroupsByKey: Record<
    string,
    { catalogue: StepCatalogueState; options: StepSelectorOption[] }
  > = {
    [PLANTPROTECTION_STEP_GROUP_KEY]: {
      catalogue: chemicalCatalogue,
      options: chemicalCatalogue.options,
    },
    [ADJUVANT_STEP_GROUP_KEY]: {
      catalogue: adjuvantCatalogue,
      options: adjuvantCatalogue.options,
    },
    [OPERATION_STEP_GROUP_KEY]: {
      catalogue: operationCatalogue,
      options: operationCatalogue.operationOptions,
    },
    [SOILINPUT_STEP_GROUP_KEY]: {
      catalogue: soilInputCatalogue,
      options: soilInputCatalogue.soilInputOptions,
    },
    [BIOSTIMULANT_STEP_GROUP_KEY]: {
      catalogue: biostimulantCatalogue,
      options: biostimulantCatalogue.biostimulantOptions,
    },
    [SEED_STEP_GROUP_KEY]: {
      catalogue: seedCatalogue,
      options: seedCatalogue.seedOptions,
    },
  };

  const activeCatalogueGroup = activeGroup
    ? catalogueGroupsByKey[activeGroup.key]
    : undefined;

  const isCatalogueGroup = !!activeCatalogueGroup;
  const catalogue = activeCatalogueGroup?.catalogue ?? chemicalCatalogue;
  const catalogueOptions = activeCatalogueGroup?.options ?? [];

  const filteredOptions = useMemo(() => {
    if (isCatalogueGroup) return catalogueOptions;
    const normalized = searchText.trim().toLowerCase();
    const dummyOptions = activeGroup?.options ?? [];
    if (!normalized) return dummyOptions;
    return dummyOptions.filter((option) =>
      option.label.toLowerCase().includes(normalized)
    );
  }, [isCatalogueGroup, catalogueOptions, activeGroup, searchText]);

  const clearAllSearches = useCallback(() => {
    chemicalCatalogue.clearSearch();
    adjuvantCatalogue.clearSearch();
    operationCatalogue.clearSearch();
    soilInputCatalogue.clearSearch();
    biostimulantCatalogue.clearSearch();
    seedCatalogue.clearSearch();
  }, [
    chemicalCatalogue.clearSearch,
    adjuvantCatalogue.clearSearch,
    operationCatalogue.clearSearch,
    soilInputCatalogue.clearSearch,
    biostimulantCatalogue.clearSearch,
    seedCatalogue.clearSearch,
  ]);

  // Only searches the catalogue for the given group key, leaving all other
  // catalogues unchanged.
  const onSearchForKey = useCallback(
    (groupKey: string, value: string) => {
      const searchFns: Record<string, (v: string) => void> = {
        [PLANTPROTECTION_STEP_GROUP_KEY]: chemicalCatalogue.onSearch,
        [ADJUVANT_STEP_GROUP_KEY]: adjuvantCatalogue.onSearch,
        [OPERATION_STEP_GROUP_KEY]: operationCatalogue.onSearch,
        [SOILINPUT_STEP_GROUP_KEY]: soilInputCatalogue.onSearch,
        [BIOSTIMULANT_STEP_GROUP_KEY]: biostimulantCatalogue.onSearch,
        [SEED_STEP_GROUP_KEY]: seedCatalogue.onSearch,
      };
      searchFns[groupKey]?.(value);
    },
    [
      chemicalCatalogue.onSearch,
      adjuvantCatalogue.onSearch,
      operationCatalogue.onSearch,
      soilInputCatalogue.onSearch,
      biostimulantCatalogue.onSearch,
      seedCatalogue.onSearch,
    ]
  );

  return {
    isCatalogueGroup,
    catalogue,
    filteredOptions,
    clearAllSearches,
    onSearchForKey,
  };
};
